import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Plus, Receipt, Euro, TrendingDown, TrendingUp, Upload, Landmark,
  RefreshCw, Trash2, FileText, X, Calendar, Building2, ScanLine,
  ShoppingCart, Users, Home, Wrench, Megaphone, Package, Coffee, MoreHorizontal,
  Tag, Palette, Edit2, Mail, Paperclip
} from 'lucide-react';
import { toast } from 'sonner';
import { ScanPagesPicker } from './ScanPagesPicker';
import { PaymentControl } from './Banks/PaymentControl';
import { VendorSelect } from './ExpenseDetail/VendorSelect';
import { PeriodInvoices } from './ExpenseDetail/PeriodInvoices';
import { madridParts } from '../../lib/utils';

const PERIOD_LABELS = { hoy: 'de hoy', semana: 'de los últimos 7 días', mes: 'de los últimos 30 días', 'año': 'del último año', rango: 'del periodo elegido' };
import { useNavigate } from 'react-router-dom';
import GmailConfigPanel from './GmailConfigPanel';
import { VatQuarterly } from './ExpenseInsights/VatQuarterly';
import { ExpenseAlerts } from './ExpenseInsights/ExpenseAlerts';
import { VendorDuplicates } from './ExpenseInsights/VendorDuplicates';
import { UnrecognizedInvoices } from './ExpenseInsights/UnrecognizedInvoices';
import { DateRangePicker, rangeQuery } from './common/DateRangePicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const invoiceHref = (url) => (url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export const DEFAULT_CATEGORY_CONFIG = {
  bebidas: { label: 'Bebidas', icon: Coffee, color: '#9333EA' },
  alimentos: { label: 'Alimentos', icon: ShoppingCart, color: '#EA580C' },
  suministros: { label: 'Suministros', icon: Package, color: '#0891B2' },
  personal: { label: 'Personal', icon: Users, color: '#059669' },
  alquiler: { label: 'Alquiler', icon: Home, color: '#DC2626' },
  servicios: { label: 'Servicios', icon: FileText, color: '#7C3AED' },
  mantenimiento: { label: 'Mantenimiento', icon: Wrench, color: '#CA8A04' },
  marketing: { label: 'Marketing', icon: Megaphone, color: '#DB2777' },
  otros: { label: 'Otros', icon: MoreHorizontal, color: '#6B7280' }
};

const ExpensesPanel = () => {
  const [gastos, setGastos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showScanDialog, setShowScanDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [periodo, setPeriodo] = useState(() => sessionStorage.getItem('gastos_periodo') || 'mes');
  useEffect(() => { sessionStorage.setItem('gastos_periodo', periodo); }, [periodo]);
  const [range, setRange] = useState(() => {
    const saved = sessionStorage.getItem('gastos_rango');
    if (saved) return JSON.parse(saved);
    const n = new Date();
    const p = (x) => String(x).padStart(2, '0');
    return { from: `${n.getFullYear()}-${p(n.getMonth() + 1)}-01`, to: `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}` };
  });
  useEffect(() => { sessionStorage.setItem('gastos_rango', JSON.stringify(range)); }, [range]);
  const rangeQs = periodo === 'rango' ? rangeQuery(range) : '';
  const listQs = rangeQs || `fecha_inicio=${madridParts(new Date(Date.now() - ({ hoy: 0, semana: 7, mes: 30, 'año': 365 }[periodo] ?? 30) * 86400000).toISOString()).date}`;
  const [customCategories, setCustomCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6B7280');
  const [editingExpense, setEditingExpense] = useState(null);
  const [scanFileName, setScanFileName] = useState('');
  const [gmailPendingCount, setGmailPendingCount] = useState(0);
  const attachInputRef = useRef(null);
  const attachTargetRef = useRef(null);
  const navigate = useNavigate();

  const attachInvoice = (expenseId) => {
    attachTargetRef.current = expenseId;
    attachInputRef.current?.click();
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const expenseId = attachTargetRef.current;
    if (!file || !expenseId) return;
    const fd = new FormData();
    fd.append('file', file);
    const t = toast.loading('Subiendo factura…');
    try {
      const res = await fetch(`${API_URL}/api/admin/gastos/${expenseId}/factura`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Error al subir');
      toast.success('Factura adjuntada', { id: t });
      if (editingExpense?.id === expenseId) setFormData(p => ({ ...p, invoice_url: j.invoice_url }));
      fetchData();
    } catch (err) {
      toast.error(err.message, { id: t });
    }
  };
  
  // Build full category config
  const CATEGORY_CONFIG = { ...DEFAULT_CATEGORY_CONFIG };
  customCategories.forEach(cat => {
    CATEGORY_CONFIG[cat.slug] = { 
      label: cat.nombre, 
      icon: Tag, 
      color: cat.color,
      isCustom: true 
    };
  });
  
  // Form state
  const [formData, setFormData] = useState({
    category: 'otros',
    amount: '',
    vendor: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    invoice_url: null,
    base_amount: '',
    vat_amount: '',
    invoice_number: '',
    vendor_nif: ''
  });

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [gastosRes, statsRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/gastos?limit=3000&${listQs}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/gastos/stats?periodo=${periodo}&${rangeQs}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/gastos/categorias`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (gastosRes.ok) {
        const data = await gastosRes.json();
        setGastos(data.gastos);
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      if (catsRes.ok) {
        const catsData = await catsRes.json();
        setCustomCategories(catsData.custom || []);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  }, [periodo, rangeQs, listQs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Introduce un importe válido');
      return;
    }

    const token = localStorage.getItem('admin_token');
    const isEditing = !!editingExpense;
    
    try {
      const url = isEditing 
        ? `${API_URL}/api/admin/gastos/${editingExpense.id}`
        : `${API_URL}/api/admin/gastos`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          base_amount: formData.base_amount === '' ? null : parseFloat(formData.base_amount),
          vat_amount: formData.vat_amount === '' ? null : parseFloat(formData.vat_amount),
          invoice_number: formData.invoice_number || null,
          vendor_nif: formData.vendor_nif || null
        })
      });

      if (response.ok) {
        toast.success(isEditing ? 'Gasto actualizado' : 'Gasto registrado');
        setShowAddDialog(false);
        setEditingExpense(null);
        resetForm();
        fetchData();
      } else {
        toast.error('Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleEdit = (gasto) => {
    setEditingExpense(gasto);
    setFormData({
      category: gasto.category || 'otros',
      amount: gasto.amount?.toString() || '',
      vendor: gasto.vendor || '',
      description: gasto.description || '',
      date: gasto.date || new Date().toISOString().split('T')[0],
      notes: gasto.notes || '',
      invoice_url: gasto.invoice_url || null,
      base_amount: gasto.base_amount ?? '',
      vat_amount: gasto.vat_amount ?? '',
      invoice_number: gasto.invoice_number || '',
      vendor_nif: gasto.vendor_nif || ''
    });
    setShowAddDialog(true);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Introduce un nombre para la categoría');
      return;
    }
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gastos/categorias?nombre=${encodeURIComponent(newCategoryName)}&color=${encodeURIComponent(newCategoryColor)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Categoría creada');
        setNewCategoryName('');
        setNewCategoryColor('#6B7280');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear categoría');
      }
    } catch (error) {
      toast.error('Error al crear categoría');
    }
  };

  const handleDeleteCategory = async (slug) => {
    if (!window.confirm('¿Eliminar esta categoría? Los gastos con esta categoría pasarán a "Otros"')) return;
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gastos/categorias/${slug}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Categoría eliminada');
        fetchData();
      } else {
        toast.error('Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gastos/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Gasto eliminado');
        fetchData();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleScanInvoice = async (file, multiFiles = null) => {
    if (!file && !multiFiles) return;
    
    const isPdf = file && (file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf'));
    setScanFileName(multiFiles ? `${multiFiles.length} páginas` : (file.name || (isPdf ? 'documento.pdf' : 'imagen')));
    setScanning(true);
    const token = localStorage.getItem('admin_token');
    
    const formDataUpload = new FormData();
    if (multiFiles) multiFiles.forEach((f, i) => formDataUpload.append('files', f, `pagina-${i + 1}.jpg`));
    else formDataUpload.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/gastos/${multiFiles ? 'scan-invoice-multi' : 'scan-invoice'}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const extracted = data.extracted;
        const pageInfo = data.pages > 1 ? ` (${data.pages} págs)` : '';
        
        setFormData(prev => ({
          ...prev,
          category: extracted.category || 'otros',
          amount: extracted.amount?.toString() || '',
          vendor: extracted.vendor || '',
          description: extracted.description || '',
          date: extracted.date || prev.date,
          invoice_url: data.invoice_url,
          base_amount: extracted.base_amount ?? '',
          vat_amount: extracted.vat_amount ?? '',
          invoice_number: extracted.invoice_number || '',
          vendor_nif: extracted.vendor_nif || ''
        }));
        
        toast.success(`Datos extraídos correctamente${pageInfo}`);
        setShowScanDialog(false);
        setScanFileName('');
        setShowAddDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al escanear');
      }
    } catch (error) {
      toast.error('Error al procesar la factura');
    } finally {
      setScanning(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'otros',
      amount: '',
      vendor: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      invoice_url: null
    });
    setEditingExpense(null);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Gastos</h2>
          <p className="text-sm text-gray-500">Control de gastos del negocio</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowCategoryDialog(true)} 
            variant="outline"
            size="sm"
            className="hidden sm:flex items-center gap-2"
          >
            <Tag size={16} />
            Categorías
          </Button>
          <Button 
            onClick={() => setShowScanDialog(true)} 
            variant="outline"
            className="flex items-center gap-2"
            data-testid="expense-scan-open-btn"
          >
            <ScanLine size={18} />
            <span className="hidden sm:inline">Escanear</span>
          </Button>
          <Button 
            onClick={() => { resetForm(); setShowAddDialog(true); }}
            className="bg-red-600 hover:bg-red-700 flex items-center gap-2"
            data-testid="expense-add-btn"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Gasto</span>
          </Button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['hoy', 'semana', 'mes', 'año', 'rango'].map(p => (
          <Button
            key={p}
            variant={periodo === p ? 'default' : 'outline'}
            size="sm"
            onClick={() => setPeriodo(p)}
            className={periodo === p ? 'bg-gray-900' : ''}
            data-testid={`expense-period-${p}`}
          >
            {p === 'rango' ? 'Fechas…' : p.charAt(0).toUpperCase() + p.slice(1)}
          </Button>
        ))}
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/gastos/proveedores')} className="ml-auto shrink-0" data-testid="expense-vendors-btn">
          <Building2 size={14} className="mr-1.5" /> Proveedores
        </Button>
        <Button variant="outline" size="sm" onClick={() => navigate('/admin/gastos/pagos')} className="shrink-0" data-testid="expense-payments-btn">
          <Landmark size={14} className="mr-1.5" /> Pagos
        </Button>
      </div>
      {periodo === 'rango' && (
        <div className="bg-white rounded-xl border shadow-sm p-3">
          <DateRangePicker value={range} onChange={setRange} idPrefix="expense-range" />
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <TrendingDown className="text-red-600" size={20} />
            </div>
            <p className="text-xl md:text-2xl font-bold text-red-600">{stats.total_gastos}€</p>
            <p className="text-xs text-gray-500">Gastos</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <TrendingUp className="text-green-600" size={20} />
            </div>
            <p className="text-xl md:text-2xl font-bold text-green-600">{stats.total_ingresos}€</p>
            <p className="text-xs text-gray-500">Ingresos</p>
          </div>
          <div className={`rounded-xl p-4 shadow-sm border text-center ${stats.balance >= 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2 ${stats.balance >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <Euro className={stats.balance >= 0 ? 'text-green-600' : 'text-red-600'} size={20} />
            </div>
            <p className={`text-xl md:text-2xl font-bold ${stats.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.balance >= 0 ? '+' : ''}{stats.balance}€
            </p>
            <p className="text-xs text-gray-500">Balance</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Receipt className="text-blue-600" size={20} />
            </div>
            <p className="text-xl md:text-2xl font-bold text-gray-900">{stats.num_gastos}</p>
            <p className="text-xs text-gray-500">Registros</p>
          </div>
        </div>
      )}

      <PeriodInvoices rows={gastos} periodLabel={PERIOD_LABELS[periodo] || ''} config={CATEGORY_CONFIG} onChanged={fetchData} onEdit={handleEdit} />

      {/* Invoices module: Gmail, automatic accounts, imports, unrecognized */}
      <section className="rounded-2xl border-2 border-blue-100 bg-blue-50/40 p-3 space-y-4" data-testid="invoices-module">
        <h2 className="px-1 text-lg font-bold text-gray-900 flex items-center gap-2"><Mail size={18} className="text-blue-600" /> Facturas por correo</h2>
        <UnrecognizedInvoices onChanged={fetchData} />
        <GmailConfigPanel onPendingCountChange={setGmailPendingCount} />
      </section>

      {/* Insights at the end */}
      <ExpenseAlerts />
      <VendorDuplicates onMerged={fetchData} />
      <VatQuarterly />

      {/* Add/Edit Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} />
              {editingExpense ? 'Editar Gasto' : formData.invoice_url ? 'Gasto desde factura' : 'Nuevo Gasto'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Category */}
            <div>
              <Label className="text-sm text-gray-600">Categoría</Label>
              <Select value={formData.category} onValueChange={v => setFormData(p => ({ ...p, category: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <config.icon size={16} style={{ color: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-gray-400 mt-1">Si el proveedor tiene categoría, la factura irá siempre a la suya.</p>
            </div>

            {/* Amount */}
            <div>
              <Label className="text-sm text-gray-600">Importe (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={formData.amount}
                onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))}
                className="text-xl font-bold"
              />
            </div>

            {/* Vendor */}
            <div>
              <Label className="text-sm text-gray-600">Proveedor</Label>
              <div className="grid grid-cols-3 gap-2">
                <VendorSelect
                  value={formData.vendor}
                  onSelect={v => setFormData(p => ({ ...p, vendor: v.name, vendor_nif: p.vendor_nif || v.nifs?.[0] || '', category: v.category || p.category }))}
                />
                <Input
                  placeholder="NIF/CIF"
                  value={formData.vendor_nif}
                  onChange={e => setFormData(p => ({ ...p, vendor_nif: e.target.value.toUpperCase() }))}
                  data-testid="expense-form-vendor-nif"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label className="text-sm text-gray-600">Descripción</Label>
              <Input
                placeholder="Concepto"
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
              />
            </div>

            {/* Date */}
            <div>
              <Label className="text-sm text-gray-600">Fecha</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={e => setFormData(p => ({ ...p, date: e.target.value }))}
              />
            </div>

            {/* Tax details */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-sm text-gray-600">Base (€)</Label>
                <Input type="number" step="0.01" value={formData.base_amount}
                  onChange={e => setFormData(p => ({ ...p, base_amount: e.target.value }))} data-testid="expense-form-base" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">IVA (€)</Label>
                <Input type="number" step="0.01" value={formData.vat_amount}
                  onChange={e => setFormData(p => ({ ...p, vat_amount: e.target.value }))} data-testid="expense-form-vat" />
              </div>
              <div>
                <Label className="text-sm text-gray-600">Nº factura</Label>
                <Input value={formData.invoice_number}
                  onChange={e => setFormData(p => ({ ...p, invoice_number: e.target.value }))} data-testid="expense-form-invoice-number" />
              </div>
            </div>

            {editingExpense && (
              <button type="button" onClick={() => attachInvoice(editingExpense.id)}
                className="w-full p-2.5 border border-dashed rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                data-testid="expense-form-attach-btn">
                <Paperclip size={15} /> {formData.invoice_url ? 'Reemplazar factura' : 'Adjuntar factura (PDF o foto)'}
              </button>
            )}

            {/* Invoice preview */}
            {formData.invoice_url && (
              <a href={invoiceHref(formData.invoice_url)}
                target="_blank" rel="noopener noreferrer"
                className="p-3 bg-blue-50 rounded-lg flex items-center gap-2 hover:bg-blue-100 transition-colors"
                data-testid="expense-form-invoice-link">
                <FileText size={18} className="text-blue-600" />
                <span className="text-sm text-blue-700">Ver factura adjunta</span>
              </a>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700">
              {editingExpense ? 'Actualizar' : 'Guardar Gasto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Invoice Dialog */}
      <Dialog open={showScanDialog} onOpenChange={setShowScanDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine size={20} />
              Escanear Factura
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6">
            {scanning ? (
              <div className="text-center py-8">
                <RefreshCw className="animate-spin mx-auto mb-4 text-blue-500" size={40} />
                <p className="text-gray-600">Analizando factura...</p>
                {scanFileName && (
                  <p className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1">
                    <FileText size={14} />
                    {scanFileName}
                  </p>
                )}
                <p className="text-sm text-gray-400 mt-2">La IA está extrayendo los datos</p>
              </div>
            ) : (
              <ScanPagesPicker onSingle={f => handleScanInvoice(f)} onMulti={fs => handleScanInvoice(null, fs)} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag size={20} />
              Gestionar Categorías
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Default categories */}
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Categorías predefinidas</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(DEFAULT_CATEGORY_CONFIG).map(([key, config]) => (
                  <Badge 
                    key={key} 
                    variant="outline" 
                    className="flex items-center gap-1"
                    style={{ borderColor: config.color, color: config.color }}
                  >
                    <config.icon size={12} />
                    {config.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Custom categories */}
            {customCategories.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500 mb-2">Tus categorías</p>
                <div className="space-y-2">
                  {customCategories.map(cat => (
                    <div key={cat.slug} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-medium">{cat.nombre}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteCategory(cat.slug)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Add new category */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Añadir nueva categoría</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Nombre"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                  className="flex-1"
                />
                <input
                  type="color"
                  value={newCategoryColor}
                  onChange={e => setNewCategoryColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border-0"
                />
                <Button onClick={handleAddCategory} size="sm">
                  <Plus size={16} />
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPanel;
